import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Seo from '../components/Seo';
import HeroSection from '../components/ui/HeroSection';
import MarqueeStrip from '../components/ui/MarqueeStrip';
import StatCard from '../components/ui/StatCard';
import VideoPlayer from '../components/ui/VideoPlayer';
import { useRevealAll } from '../hooks/useReveal';
import { useCountdown } from '../hooks/useCountdown';
import { getArticles, getAppelActif, getTemoignages, getAnnuaire, subscribeNewsletter } from '../services/api';
import { formatDate } from '../utils/date';
import { LOGO_DIMS } from '../utils/logoDims';
import './Home.css';

function CohorteBanner({ appel, prochainAppel }) {
  const { t, i18n } = useTranslation('home');
  const isOpen = !!appel;
  const target = isOpen ? appel?.dateCloture : prochainAppel?.dateOuverture;
  const countdown = useCountdown(target);

  const deadline = appel?.dateCloture
    ? formatDate(appel.dateCloture, i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const nomCohorte = (isOpen ? appel?.cohorte?.nom || appel?.titre : prochainAppel?.cohorte?.nom || prochainAppel?.titre) || t('cohort_banner.default_name');

  const benefits = [t('benefits.b1'), t('benefits.b2'), t('benefits.b3'), t('benefits.b4')];

  return (
    <section className={`hcb-section${isOpen ? ' hcb-open' : ' hcb-coming'}`}>
      <div className="hcb-bg" />
      <div className="hcb-grain" />
      <div className="container hcb-inner">

        <div className="hcb-left">
          {isOpen ? (
            <div className="hcb-badge hcb-badge-open">
              <span className="hcb-dot" />
              {t('cohort_banner.open_label')}
            </div>
          ) : (
            <div className="hcb-badge hcb-badge-coming">
              ⏳ {t('cohort_banner.next_label')}
            </div>
          )}

          <h2 className="hcb-title">
            {nomCohorte}
            <br />
            <em>{isOpen ? t('cohort_banner.open_title') : t('cohort_banner.next_title')}</em>
          </h2>

         

          <p className="hcb-desc">
            {isOpen
              ? (appel?.description || t('cohort_banner.desc'))
              : t('cohort_banner.alert_desc')}
          </p>

          <div className="hcb-actions">
            {isOpen ? (
              <>
                <Link to="/candidature" className="hcb-btn-primary">
                  {t('cohort_banner.cta_apply')}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </Link>
                <Link to="/appel" className="hcb-btn-ghost">{t('cohort_banner.cta_more')}</Link>
              </>
            ) : (
              <a href="#newsletter" className="hcb-btn-primary">
                {t('cohort_banner.cta_alert')}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </a>
            )}
          </div>
        </div>

        <div className="hcb-right">
          {countdown && !countdown.expired && (
            <div className="hcb-countdown-wrap">
              <div className="hcb-countdown-label">
                {isOpen ? t('cohort_banner.countdown_apply') : t('cohort_banner.countdown_next')}
              </div>
              <div className="hcb-countdown">
                {[
                  { val: countdown.jours,    lbl: t('cohort_banner.days') },
                  { val: countdown.heures,   lbl: t('cohort_banner.hours') },
                  { val: countdown.minutes,  lbl: t('cohort_banner.min') },
                  { val: countdown.secondes, lbl: t('cohort_banner.sec') },
                ].map(({ val, lbl }) => (
                  <div key={lbl} className="hcb-cd-unit">
                    <span className="hcb-cd-num">{String(val).padStart(2, '0')}</span>
                    <span className="hcb-cd-lbl">{lbl}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isOpen && deadline && (
            <div className="hcb-deadline-card">
              <div className="hcb-deadline-icon">📅</div>
              <div>
                <div className="hcb-deadline-label">{t('cohort_banner.deadline_card_label')}</div>
                <div className="hcb-deadline-date">{deadline}</div>
              </div>
            </div>
          )}

          <div className="hcb-benefits">
            {benefits.map((text) => (
              <div key={text} className="hcb-benefit">
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Entrepreneuses carousel ──────────────────────────────────────────────────

const ENT_COLORS = {
  COMMERCE: 'magenta', AGRICULTURE: 'teal',    SERVICES: 'orange',
  ARTISANAT: 'navy',   NUMERIQUE:   'magenta', SANTE:    'teal',
  EDUCATION: 'orange', AUTRE:       'navy',
};
const ENT_LABELS = {
  COMMERCE: 'Commerce', AGRICULTURE: 'Agriculture', SERVICES: 'Services',
  ARTISANAT: 'Artisanat', NUMERIQUE: 'Numérique', SANTE: 'Santé',
  EDUCATION: 'Éducation', AUTRE: 'Autre',
};
const ENT_ZONE_SLUGS = { DAKAR: 'dakar', THIES: 'thies', AUTRE: 'autre' };

function EntCard({ part }) {
  const { t }      = useTranslation('home');
  const color      = ENT_COLORS[part.secteur] || 'magenta';
  const regionSlug = ENT_ZONE_SLUGS[part.cohorte?.zone] || 'dakar';
  const link       = part.cohorte
    ? `/cohortes/${regionSlug}/${part.cohorte.id}/participante/${part.id}`
    : '/annuaire';
  const initiales  = ((part.prenom?.[0] || '') + (part.nom?.[0] || '')).toUpperCase();

  return (
    <Link to={link} className="h-ent-card" draggable={false}>
      {part.photoUrl ? (
        <img
          src={part.photoUrl}
          alt={`${part.prenom} ${part.nom}`}
          className={`h-ent-avatar h-ent-photo ${color}`}
          draggable={false}
        />
      ) : (
        <div className={`h-ent-avatar h-ent-initials ${color}`}>{initiales}</div>
      )}
      <div className="h-ent-info">
        <div className="h-ent-name">{part.prenom} {part.nom}</div>
        {part.nomEntreprise ? (
          <div className="h-ent-company">{part.nomEntreprise}</div>
        ) : part.cohorte && (
          <div className="h-ent-company">{t('directory.cohorte', { numero: part.cohorte.numero })}</div>
        )}
        {!part.visibleAnnuaire ? (
          <span className="h-ent-badge h-ent-badge-pending">{t('directory.profile_pending')}</span>
        ) : part.secteur && (
          <span className={`h-ent-badge h-ent-badge-${color}`}>
            {ENT_LABELS[part.secteur] || part.secteur}
          </span>
        )}
      </div>
    </Link>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Home() {
  const { t, i18n } = useTranslation('home');
  const pageRef = useRevealAll();
  const [nlEmail, setNlEmail]     = useState('');
  const [nlName,  setNlName]      = useState('');
  const [nlState, setNlState]     = useState('idle');
  const [nlError, setNlError]     = useState('');
  const [recentArticles, setRecentArticles] = useState([]);
  const [appelInfo, setAppelInfo] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [testiIdx, setTestiIdx] = useState(0);
  const [testiPaused, setTestiPaused] = useState(false);
  const [testiVideoPlaying, setTestiVideoPlaying] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [annuaireItems, setAnnuaireItems] = useState([]);

  const PROGRAMS = [
    { num: '01', title: t('programs.p1_title'), desc: t('programs.p1_desc'), href: '/programmes#inclusion' },
    { num: '02', title: t('programs.p2_title'), desc: t('programs.p2_desc'), href: '/programmes#entrepreneuriat' },
    { num: '03', title: t('programs.p3_title'), desc: t('programs.p3_desc'), href: '/programmes#formation' },
    { num: '04', title: t('programs.p4_title'), desc: t('programs.p4_desc'), href: '/programmes#agriculture' },
    { num: '05', title: t('programs.p5_title'), desc: t('programs.p5_desc'), href: '/programmes#mentorat' },
    { num: '06', title: t('programs.p6_title'), desc: t('programs.p6_desc'), href: '/programmes#innovation' },
  ];

  const PARTNERS = [
    {
      name: 'Affaires mondiales Canada & Government of New Brunswick',
      role: t('partners.p1_role'),
      desc: t('partners.p1_desc'),
      logos: ['/logo-partenaire/Affaires-mondiales-Canada.jpg', '/logo-partenaire/Government of New Brunswick.png'],
      url: 'https://www.canada.ca/fr.html',
    },
    {
      name: 'Savoir Sphere Canada',
      role: t('partners.p2_role'),
      desc: t('partners.p2_desc'),
      logos: ['/logo-partenaire/logo savoir sphere.png'],
      url: 'https://savoirsphere.ca/',
    },
    {
      name: 'CECI',
      role: t('partners.p3_role'),
      desc: t('partners.p3_desc'),
      logos: ['/logo-partenaire/CECI.png'],
      url: 'https://ceci.org/',
    },
    {
      name: 'Association des Femmes sénégalaises dans le Secteur du Numérique',
      role: t('partners.p4_role'),
      desc: t('partners.p4_desc'),
      logos: ['/logo-partenaire/logo_festic.png'],
      url: 'https://www.festicsn.com/fr',
    },
  ];

  const MARQUEE_ITEMS = [
    t('marquee.autonomy'),
    t('marquee.inclusion'),
    t('marquee.entrepreneurship'),
    t('marquee.transformation'),
    t('marquee.countries'),
  ];

  const NL_BENEFITS = [
    { title: t('resources.r1_title'), desc: t('resources.r1_desc') },
    { title: t('resources.r2_title'), desc: t('resources.r2_desc') },
    { title: t('resources.r3_title'), desc: t('resources.r3_desc') },
  ];

  const getYtId = url => url?.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|live\/|embed\/)|youtu\.be\/)([^&\n?#]+)/)?.[1] ?? null;

  function goTesti(i) {
    if (testimonials.length === 0) return;
    setTestiIdx((i + testimonials.length) % testimonials.length);
    setTestiVideoPlaying(false);
  }

  useEffect(() => {
    getTemoignages()
      .then(res => setTestimonials(res?.temoignages ?? []))
      .catch(() => setTestimonials([]));
  }, []);

  useEffect(() => {
    getAnnuaire({ pageSize: 24 })
      .then(res => setAnnuaireItems(res?.participantes ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (testiPaused || testimonials.length === 0) return;
    const timer = setInterval(() => setTestiIdx(i => (i + 1) % testimonials.length), 5500);
    return () => clearInterval(timer);
  }, [testiPaused, testimonials.length]);

  useEffect(() => {
    document.body.style.overflow = videoOpen ? 'hidden' : '';
    const onKey = e => { if (e.key === 'Escape') setVideoOpen(false); };
    if (videoOpen) window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [videoOpen]);

  useEffect(() => {
    if (nlState !== 'success') return;
    const timer = setTimeout(() => {
      setNlState('idle');
      setNlEmail('');
      setNlName('');
      setNlError('');
    }, 3000);
    return () => clearTimeout(timer);
  }, [nlState]);

  async function handleNlSubmit(e) {
    e.preventDefault();
    if (!nlEmail) return;
    setNlState('loading');
    setNlError('');
    try {
      await subscribeNewsletter(nlEmail, nlName.trim() || undefined);
      setNlState('success');
    } catch (err) {
      setNlState('idle');
      if (err.code === 'ALREADY_SUBSCRIBED') {
        setNlError(t('newsletter.already_subscribed'));
      } else {
        setNlError(err?.message || t('newsletter.error_generic'));
      }
    }
  }

  useEffect(() => {
    getArticles({ limit: 3, page: 1 })
      .then(res => setRecentArticles(res.articles || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getAppelActif()
      .then(res => {
        if (res?.appel || res?.prochainAppel) setAppelInfo(res);
      })
      .catch(() => {});
  }, []);

  return (
    <div ref={pageRef} className="home-page">
      <Seo description={t('meta.description')} path="/" />
      {/* ── HERO ── */}
      <HeroSection />

      {/* ── MARQUEE ── */}
      <MarqueeStrip items={MARQUEE_ITEMS} />

      {/* ── FESTIC ── */}
      <section className="festic-section reveal">
        <div className="container">
          <div className="festic-inner">
            <div className="festic-logo-col">
              <a href="https://www.festicsn.com/fr" target="_blank" rel="noopener noreferrer">
                <img src="/logo-partenaire/logo_festic.png" alt="FESTIC" className="festic-logo" />
              </a>
            </div>
            <div className="festic-body">
              <div className="eyebrow">{t('festic.eyebrow')}</div>
              <h2 className="section-title">
                {t('festic.title_prefix')} <em>FESTIC</em>
              </h2>
              <p className="festic-desc">
                {t('festic.desc')}
              </p>
              <div className="festic-tags">
                <span>{t('festic.tag1')}</span>
                <span>{t('festic.tag2')}</span>
                <span>{t('festic.tag3')}</span>
              </div>
              <a
                href="https://www.festicsn.com/fr"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
                style={{ marginTop: 28, display: 'inline-block' }}
              >
                {t('festic.cta')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION ── */}
      <section className="section" style={{ paddingBottom: 40 }}>
        <div className="container">
          <div className="cols-2 mission-grid">
            <div className="reveal mission-col-left">
              <div className="eyebrow">{t('mission.label')}</div>
              <h2 className="section-title">{t('mission.title').split(t('mission.title_em'))[0]}<em>{t('mission.title_em')}</em>{t('mission.title').split(t('mission.title_em'))[1]}</h2>
              <button className="mvt" onClick={() => setVideoOpen(true)}>
                <div className="mvt-thumb" style={{ backgroundImage: 'url(/videos/image-poster.webp)' }}>
                  <div className="mvt-overlay" />
                  <div className="mvt-play">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  </div>
                </div>
                <span className="mvt-label">{t('mission.cta')}</span>
              </button>
            </div>
            <div className="reveal reveal-d2">
              <p style={{ fontSize: 19, lineHeight: 1.75, color: '#000', marginBottom: 24, textAlign: 'justify' }}>
                <strong style={{ color: 'var(--navy)' }}>L'envol digital pour elles</strong> {t('mission.desc1')}
              </p>
              <blockquote className="pull">
                {t('mission.quote')}
              </blockquote>
              <p style={{ fontSize: 19, lineHeight: 1.75, color: '#000', textAlign: 'justify' }}>
                {t('mission.desc2')}
              </p>
              <Link to="/a-propos#projet" className="btn-ghost" style={{ marginTop: 32, display: 'inline-block' }}>
                {t('mission.learn_more')} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="stats-wrap">
        <div className="container">
          <div className="reveal">
            <div className="eyebrow">{t('stats.label')}</div>
            <h2 className="section-title">
              {t('stats.title').split(t('stats.title_em'))[0]}<em style={{ color: 'var(--orange)' }}>{t('stats.title_em')}</em>{t('stats.title').split(t('stats.title_em'))[1]}
            </h2>
          </div>
          <div className="stats-grid">
            <StatCard to={1000} label={t('stats.s1_label')} desc={t('stats.s1_sub')} />
            <StatCard to={10000} label={t('stats.s2_label')} desc={t('stats.s2_sub')} delay={2} />
            <StatCard to={2} label={t('stats.s3_label')} desc={t('stats.s3_sub')} delay={3} />
            <StatCard to={2} suffix={t('stats.s4_suffix')} label={t('stats.s4_label')} desc={t('stats.s4_sub')} delay={4} />
          </div>
        </div>
      </section>

      {/* ── COHORTE ANNONCE ── */}
      {appelInfo && (
        <CohorteBanner
          appel={appelInfo.appel}
          prochainAppel={appelInfo.prochainAppel}
        />
      )}

      {/* ── PROGRAMMES ── */}
      <section className="section">
        <div className="container">
          <div className="reveal" style={{ maxWidth: 780 }}>
            <div className="eyebrow">{t('programs.label')}</div>
            <h2 className="section-title" style={{ maxWidth: '28ch' }}>{t('programs.title').split(t('programs.title_em'))[0]}<em>{t('programs.title_em')}</em>{t('programs.title').split(t('programs.title_em'))[1]}</h2>
            <p className="section-lead" style={{ maxWidth: 'none' }}>{t('programs.desc')}</p>
          </div>
          <div className="grid-3" style={{ marginTop: 64 }}>
            {PROGRAMS.map((p, i) => (
              <Link key={p.num} to={p.href} className={`card reveal${i % 3 !== 0 ? ` reveal-d${(i % 3) + 1}` : ''}`} style={{ textDecoration: 'none' }}>
                <div className="card-num">{p.num}</div>
                <h3 className="card-title">{p.title}</h3>
                <p>{p.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── TÉMOIGNAGES ── */}
      {testimonials.length > 0 && (() => {
        const item = testimonials[testiIdx];
        const ytId = getYtId(item.videoUrl);
        const isLocal = item.videoUrl?.includes('/uploads/');
        const hasVideo = !!item.videoUrl;
        return (
          <section
            className="testi"
            onMouseEnter={() => setTestiPaused(true)}
            onMouseLeave={() => setTestiPaused(false)}
          >
            <div className="testi-bg" />
            <div className="container testi-wrap">
              <div className="testi-stage">
                <div className={`testi-card${hasVideo ? ' testi-card--video' : ''}`} key={testiIdx}>

                  {/* ── Panneau vidéo ── */}
                  {hasVideo && (
                    <div className="testi-video-pane">
                      {testiVideoPlaying ? (
                        ytId ? (
                          <iframe
                            className="testi-iframe"
                            src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1`}
                            allow="fullscreen; picture-in-picture; encrypted-media"
                            allowFullScreen
                            frameBorder="0"
                            title={t('testimonials.video_title')}
                          />
                        ) : isLocal ? (
                          <video
                            className="testi-video-el"
                            src={item.videoUrl}
                            controls
                            autoPlay
                          />
                        ) : null
                      ) : (
                        <div
                          className="testi-video-thumb"
                          onClick={() => { setTestiVideoPlaying(true); setTestiPaused(true); }}
                          role="button"
                          aria-label={t('testimonials.play_aria')}
                        >
                          {ytId ? (
                            <img
                              src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                              alt=""
                              width="480"
                              height="360"
                              loading="lazy"
                              decoding="async"
                              className="testi-thumb-img"
                            />
                          ) : (
                            <div className="testi-thumb-placeholder">
                              {item.photoUrl && <img src={item.photoUrl} alt="" className="testi-thumb-portrait" />}
                            </div>
                          )}
                          <div className="testi-play-overlay">
                            <div className="testi-play-circle">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                            </div>
                            <span className="testi-play-label">{t('testimonials.watch_label')}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Panneau contenu ── */}
                  <div className="testi-content-pane">
                    <div className="testi-qmark">"</div>
                    <p className="testi-quote">{item.citation}</p>
                    <div className="testi-sep" />
                    <div className="testi-author">
                      {item.photoUrl ? (
                        <img
                          src={item.photoUrl}
                          alt={item.nom}
                          className={`testi-avatar testi-av-photo testi-av-${item.couleur}`}
                        />
                      ) : (
                        <div className={`testi-avatar testi-av-${item.couleur}`}>
                          {item.initiales}
                        </div>
                      )}
                      <div className="testi-meta">
                        <div className="testi-name">{item.nom}</div>
                        <div className="testi-subline">
                          {[item.role, item.cohorte].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      {item.programme && <span className="testi-tag">{item.programme}</span>}
                    </div>
                  </div>

                </div>
              </div>

              {/* Navigation */}
              <div className="testi-nav">
                <button className="testi-btn" onClick={() => goTesti(testiIdx - 1)} aria-label={t('testimonials.prev_aria')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                </button>
                <div className="testi-dots">
                  {testimonials.map((_, i) => (
                    <button key={i} className={`testi-dot${i === testiIdx ? ' active' : ''}`} onClick={() => goTesti(i)} aria-label={t('testimonials.dot_aria', { n: i + 1 })} />
                  ))}
                </div>
                <button className="testi-btn" onClick={() => goTesti(testiIdx + 1)} aria-label={t('testimonials.next_aria')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
              </div>

              <div className="testi-progress-bar">
                <div className="testi-progress-fill" style={{ width: `${((testiIdx + 1) / testimonials.length) * 100}%` }} />
              </div>
            </div>
          </section>
        );
      })()}

      {/* ── PARTENAIRES ── */}
      <section className="section prt-section">
        <div className="container">
          <div className="reveal" style={{ textAlign: 'center', marginBottom: 64 }}>
            <div className="eyebrow center">{t('partners.label')}</div>
            <h2 className="section-title center">
              {t('partners.title').split(t('partners.title_em'))[0]}<em>{t('partners.title_em')}</em>{t('partners.title').split(t('partners.title_em'))[1]}
            </h2>
          </div>
        </div>
        <div className="prt-track-wrap reveal">
          <div className="prt-track">
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="prt-card"
              >
                <div className="prt-logo-wrap">
                  {p.logos.map((logo, li) => (
                    <img key={li} src={logo} alt={p.name} className="prt-logo" {...LOGO_DIMS[logo]} />
                  ))}
                </div>
                <div className="prt-body">
                  <span className="prt-role">{p.role}</span>
                  <div className="prt-name">{p.name}</div>
                  <p className="prt-desc">{p.desc}</p>
                </div>
                <div className="prt-arrow">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M7 17L17 7M17 7H7M17 7v10"/>
                  </svg>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── ARTICLES RÉCENTS ── */}
      {recentArticles.length > 0 && (
        <section className="section" style={{ background: '#fff' }}>
          <div className="container">
            <div style={{ marginBottom: 56 }}>
              <div className="eyebrow">{t('blog.label')}</div>
              <h2 className="section-title" style={{ maxWidth: 'none' }}>{t('blog.title').split(t('blog.title_em'))[0]}<em>{t('blog.title_em')}</em>{t('blog.title').split(t('blog.title_em'))[1]}</h2>
            </div>
            <div className="home-articles-grid">
              {recentArticles.map((article, i) => {
                const dateStr = article.publieLe
                  ? formatDate(article.publieLe, i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
                  : '';
                const auteurStr = article.auteur
                  ? typeof article.auteur === 'object'
                    ? `${article.auteur.prenom ?? ''} ${article.auteur.nom ?? ''}`.trim()
                    : String(article.auteur)
                  : '';
                return (
                  <Link
                    key={article.id}
                    to={`/actualites/article/${article.slug || article.id}`}
                    state={{ preload: { titre: article.titre, extrait: article.extrait, imageUrl: article.imageUrl } }}
                    className="home-article-card"
                  >
                    <div className="home-article-cover">
                      {article.imageUrl
                        ? <img src={article.imageUrl} alt={article.titre} />
                        : <div className={`home-article-cover-placeholder hac-ph${(i % 3) + 1}`} />
                      }
                      {article.type && (
                        <span className="home-article-cat">{article.type}</span>
                      )}
                    </div>
                    <div className="home-article-body">
                      <h3 className="home-article-title">{article.titre}</h3>
                      {article.extrait && (
                        <p className="home-article-excerpt">{article.extrait}</p>
                      )}
                      <div className="home-article-meta">
                        {auteurStr && <span>{auteurStr}</span>}
                        {auteurStr && dateStr && <span className="home-article-dot">·</span>}
                        {dateStr && <span>{dateStr}</span>}
                      </div>
                      <span className="home-article-cta">{t('blog.cta_read')} →</span>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div style={{ textAlign: 'center', marginTop: 48 }}>
              <Link
                to="/actualites#blog"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--navy)',
                  textDecoration: 'none',
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  transition: 'color .2s',
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--magenta)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--navy)'}
              >
                {t('blog.cta_all')}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── NEWSLETTER ── */}
      <section className="hnl-section" id="newsletter">
        <div className="container">
          <div className="hnl-card reveal">

            <div className="hnl-left">
              <div className="eyebrow light">{t('newsletter.label')}</div>
              <h2 className="section-title" style={{ color: '#fff', maxWidth: '13ch' }}>
                {t('newsletter.title').split(t('newsletter.title_em'))[0]}<em style={{ color: 'var(--orange)', fontStyle: 'italic' }}>{t('newsletter.title_em')}</em>{t('newsletter.title').split(t('newsletter.title_em'))[1]}
              </h2>
              <p className="hnl-lead">{t('newsletter.desc')}</p>

              <div className="hnl-benefits">
                {NL_BENEFITS.map(b => (
                  <div className="hnl-benefit" key={b.title}>
                    <div>
                      <div className="hnl-benefit-title">{b.title}</div>
                      <div className="hnl-benefit-desc">{b.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

            </div>

            <div className="hnl-right">
              <div className="hnl-form-card">
                {nlState !== 'success' ? (
                  <>
                    <h3 className="hnl-form-title">{t('newsletter.form_title')}</h3>

                    <div className="hnl-social-proof">
                      <div className="hnl-avatars">
                        {['F','A','S'].map((l,i) => (
                          <div key={l} className={`hnl-avatar hnl-av${i+1}`}>{l}</div>
                        ))}
                      </div>
                      <span className="hnl-proof-text">
                        <strong>{t('newsletter.subscribers_bold', { count: 342 })}</strong> {t('newsletter.subscribers_rest')}
                      </span>
                    </div>

                    <form className="hnl-form" onSubmit={handleNlSubmit} noValidate>
                      <div className="hnl-field">
                        <label htmlFor="nl-name">{t('newsletter.firstname')}</label>
                        <input
                          id="nl-name"
                          type="text"
                          placeholder={t('newsletter.firstname_placeholder')}
                          value={nlName}
                          onChange={e => setNlName(e.target.value)}
                          autoComplete="given-name"
                        />
                      </div>
                      <div className="hnl-field">
                        <label htmlFor="nl-email">{t('newsletter.email')} <span className="hnl-req">*</span></label>
                        <input
                          id="nl-email"
                          type="email"
                          placeholder={t('newsletter.email_placeholder')}
                          value={nlEmail}
                          onChange={e => setNlEmail(e.target.value)}
                          required
                          autoComplete="email"
                        />
                      </div>

                      <div className="hnl-prefs">
                        <div className="hnl-prefs-label">{t('newsletter.prefs')}</div>
                        {[t('newsletter.pref_news'), t('newsletter.pref_resources'), t('newsletter.pref_events')].map(p => (
                          <label key={p} className="hnl-pref">
                            <input type="checkbox" defaultChecked />
                            <span className="hnl-pref-check" />
                            {p}
                          </label>
                        ))}
                      </div>

                      <button type="submit" className="hnl-btn" disabled={nlState === 'loading'}>
                        {nlState === 'loading'
                          ? <span className="hnl-spinner" />
                          : <><span>{t('newsletter.subscribe_btn')}</span><span className="arrow">→</span></>
                        }
                      </button>
                      {nlError && (
                        <p style={{ color: '#e05', fontSize: 13, margin: '8px 0 0', textAlign: 'center' }}>{nlError}</p>
                      )}
                    </form>

                    <p className="hnl-trust">{t('newsletter.disclaimer')}</p>
                  </>
                ) : (
                  <div className="hnl-success">
                    <div className="hnl-success-check">✓</div>
                    <h3 className="hnl-success-title">{t('newsletter.success_title')}</h3>
                    <p className="hnl-success-sub">{t('newsletter.success_desc')}</p>
                    <div className="hnl-success-email">{nlEmail}</div>
                    <Link to="/actualites" className="hnl-success-link">
                      {t('newsletter.success_cta_news')} →
                    </Link>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── ENTREPRENEUSES CAROUSEL ── */}
      {annuaireItems.length > 0 && (() => {
        const fillTrack = (arr) => {
          let base = [...arr];
          while (base.length < 12) base = [...base, ...arr];
          return [...base, ...base];
        };
        const track1 = fillTrack(annuaireItems);
        const track2 = fillTrack([...annuaireItems].reverse());
        return (
          <section className="h-ent-section">
            <div className="container">
              <div className="h-ent-header">
                <div>
                  <div className="eyebrow light">{t('directory.eyebrow')}</div>
                  <h2 className="section-title" style={{ color: '#fff' }}>
                    {t('directory.title').split(t('directory.title_em'))[0]}<em style={{ color: 'var(--orange)', fontStyle: 'italic' }}>{t('directory.title_em')}</em>{t('directory.title').split(t('directory.title_em'))[1]}
                  </h2>
                  <p style={{ fontSize: 17, lineHeight: 1.7, color: '#fff', marginBottom: 0, maxWidth: 480 }}>
                    {t('directory.desc')}
                  </p>
                </div>
                <Link
                  to="/annuaire"
                  className="btn-ghost"
                  style={{ borderColor: 'rgba(255,255,255,.35)', color: '#fff', flexShrink: 0, alignSelf: 'flex-start' }}
                >
                  {t('directory.cta')}
                </Link>
              </div>
            </div>

            <div className="h-ent-tracks">
              <div className="h-ent-track-wrap">
                <div className="h-ent-track h-ent-track-left">
                  {track1.map((p, i) => (
                    <EntCard key={`t1-${p.id}-${i}`} part={p} />
                  ))}
                </div>
              </div>
              <div className="h-ent-track-wrap">
                <div className="h-ent-track h-ent-track-right">
                  {track2.map((p, i) => (
                    <EntCard key={`t2-${p.id}-${i}`} part={p} />
                  ))}
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* ── CTA ── */}
      <section className="section" style={{ paddingTop: 40 }}>
        <div className="container reveal" style={{ textAlign: 'center', maxWidth: 880, margin: '0 auto' }}>
          <div className="eyebrow center">{t('cta_section.eyebrow')}</div>
          <h2 className="section-title center">
            {t('cta_section.title').split(t('cta_section.title_em'))[0]}<em>{t('cta_section.title_em')}</em>{t('cta_section.title').split(t('cta_section.title_em'))[1]}
          </h2>
          <p className="section-lead center" style={{ marginBottom: 40 }}>
            {t('cta_section.desc')}
          </p>
          <Link to="/appel" className="btn-primary">
            {t('cohort_banner.cta_apply')} <span className="arrow">→</span>
          </Link>
        </div>
      </section>

      {/* ── VIDEO MODAL ── */}
      {videoOpen && (
        <div className="video-modal-overlay" onClick={() => setVideoOpen(false)}>
          <div className="video-modal-inner" onClick={e => e.stopPropagation()}>
            <button className="video-modal-close" onClick={() => setVideoOpen(false)} aria-label={t('video_modal.close_aria')}>
              <span /><span />
            </button>
            <VideoPlayer
              youtubeId="G8GNs_PEh20"
              poster="/videos/image-poster.webp"
              title={`${t('video_modal.title_prefix')} L'Envol Digital pour Elles`}
              subtitle={`${t('video_modal.subtitle_prefix')} 2026 – 2028 · FESTIC · Cameroun & Sénégal`}
            />
          </div>
        </div>
      )}
    </div>
  );
}
